import { useMemo, useState } from "react";
import { Check, Dumbbell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { resolveBackendAssetUrl } from "@/integrations/backend/client";
import type { Equipment } from "@/integrations/backend/equipment";
import { cn } from "@/lib/utils";
import {
  profileNestedPanelClass,
  profileNestedPanelContentClass,
  profileNestedPanelHeaderClass,
} from "@/components/profile/ProfileTabPanel";

type EquipmentAccessPanelProps = {
  equipment: Equipment[];
  loading: boolean;
  selectedIds: string[];
  editMode: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onToggle: (id: string) => void;
  onSelectionChange: (ids: string[]) => void;
};

function normalizeCategory(category: string | null): string {
  const trimmed = (category || "").trim();
  return trimmed || "Other";
}

function EquipmentThumbnail({
  item,
  size = "sm",
}: {
  item: Equipment;
  size?: "sm" | "xs";
}) {
  const [broken, setBroken] = useState(false);
  const imageUrl = resolveBackendAssetUrl(item.image_file);
  const showImage = Boolean(imageUrl) && !broken;
  const sizeClass = size === "xs" ? "h-7 w-7" : "h-9 w-9";
  const iconClass = size === "xs" ? "h-3.5 w-3.5" : "h-4 w-4";

  if (showImage) {
    return (
      <img
        src={imageUrl as string}
        alt=""
        aria-hidden="true"
        className={cn(sizeClass, "shrink-0 rounded-md border border-primary/20 object-cover")}
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
  }

  return (
    <div
      className={cn(
        sizeClass,
        "flex shrink-0 items-center justify-center rounded-md border border-primary/20 bg-dark-card"
      )}
      aria-hidden="true"
    >
      <Dumbbell className={cn(iconClass, "text-gray-light")} />
    </div>
  );
}

function CompactEditTile({
  item,
  selected,
  onToggle,
}: {
  item: Equipment;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={selected ? `Deselect ${item.name}` : `Select ${item.name}`}
      onClick={onToggle}
      className={cn(
        "flex min-h-11 w-full items-start gap-2 rounded-lg border px-2 py-2 text-left transition-all",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        selected
          ? "border-primary bg-primary/10"
          : "border-primary/20 bg-dark-slate hover:border-primary/50"
      )}
    >
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded border",
          selected
            ? "border-primary bg-primary text-black"
            : "border-primary/30 bg-dark-card"
        )}
        aria-hidden="true"
      >
        {selected ? <Check className="h-3 w-3" /> : null}
      </span>
      <EquipmentThumbnail item={item} size="xs" />
      <span className="min-w-0 flex-1 break-words text-sm font-medium leading-snug text-white">
        {item.name}
      </span>
    </button>
  );
}

function ViewChip({ item }: { item: Equipment }) {
  return (
    <Badge
      variant="outline"
      className="inline-flex h-auto max-w-full items-center gap-1.5 whitespace-normal border-primary/30 bg-dark-slate px-2 py-1 text-white"
    >
      <EquipmentThumbnail item={item} size="xs" />
      <span className="break-words">{item.name}</span>
    </Badge>
  );
}

function LoadingSkeleton({ editMode }: { editMode: boolean }) {
  return (
    <div
      className={cn(
        "grid gap-2",
        editMode
          ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          : "grid-cols-2 sm:grid-cols-3"
      )}
    >
      {Array.from({ length: editMode ? 8 : 4 }).map((_, i) => (
        <div
          key={i}
          className="h-11 animate-pulse rounded-lg border border-primary/20 bg-dark-slate"
        />
      ))}
    </div>
  );
}

export function EquipmentAccessPanel({
  equipment,
  loading,
  selectedIds,
  editMode,
  onEdit,
  onSave,
  onCancel,
  onToggle,
  onSelectionChange,
}: EquipmentAccessPanelProps) {
  const isMobile = useIsMobile();
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const selectedEquipment = useMemo(
    () => equipment.filter((item) => selectedSet.has(item.id)),
    [equipment, selectedSet]
  );

  const groupedEquipment = useMemo(() => {
    const groups = new Map<string, Equipment[]>();
    for (const item of equipment) {
      const category = normalizeCategory(item.category);
      const list = groups.get(category) ?? [];
      list.push(item);
      groups.set(category, list);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [equipment]);

  const summaryText = loading
    ? "Loading equipment..."
    : selectedIds.length === 0
      ? "None selected"
      : selectedIds.length === 1
        ? "1 selected"
        : `${selectedIds.length} selected`;

  const headerActions = editMode ? (
    <div className="flex flex-wrap gap-2">
      <Button
        size="sm"
        className="bg-primary text-black hover:bg-primary/90"
        onClick={onSave}
      >
        Save
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="border-primary/30 text-primary hover:bg-primary hover:text-black"
        onClick={onCancel}
      >
        Cancel
      </Button>
    </div>
  ) : (
    <Button
      size="sm"
      variant="outline"
      className="border-primary/30 text-primary hover:bg-primary hover:text-black"
      onClick={onEdit}
      disabled={loading}
    >
      {selectedIds.length === 0 ? "Set Equipment" : "Edit"}
    </Button>
  );

  const toggleCategory = (items: Equipment[], select: boolean) => {
    const ids = new Set(selectedIds);
    for (const item of items) {
      if (select) ids.add(item.id);
      else ids.delete(item.id);
    }
    onSelectionChange(Array.from(ids));
  };

  return (
    <Card className={profileNestedPanelClass(isMobile)}>
      <CardHeader className={profileNestedPanelHeaderClass(isMobile)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center text-white">
              <Dumbbell className="mr-2 h-5 w-5 text-primary" />
              Equipment Access
            </CardTitle>
            <CardDescription>
              <span className="text-gray-light">
                {editMode
                  ? "Tap items to toggle. Use category shortcuts to select faster."
                  : "Select the equipment you have access to"}
              </span>
            </CardDescription>
          </div>
          {headerActions}
        </div>
      </CardHeader>
      <CardContent
        className={cn("space-y-3", profileNestedPanelContentClass(isMobile))}
      >
        <div className="text-sm text-gray-light">{summaryText}</div>

        {loading ? (
          <LoadingSkeleton editMode={editMode} />
        ) : editMode ? (
          <div className="space-y-4">
            {groupedEquipment.map(([category, items]) => {
              const categorySelectedCount = items.filter((item) =>
                selectedSet.has(item.id)
              ).length;
              const allSelected = categorySelectedCount === items.length;
              const noneSelected = categorySelectedCount === 0;

              return (
                <div key={category} className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-xs font-medium uppercase tracking-wide text-gray-light">
                      {category}
                      <span className="ml-1.5 normal-case tracking-normal text-gray-light/80">
                        ({categorySelectedCount}/{items.length})
                      </span>
                    </h3>
                    <div className="flex shrink-0 gap-2">
                      {!allSelected ? (
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline"
                          onClick={() => toggleCategory(items, true)}
                        >
                          Select all
                        </button>
                      ) : null}
                      {!noneSelected ? (
                        <button
                          type="button"
                          className="text-xs text-gray-light hover:text-white hover:underline"
                          onClick={() => toggleCategory(items, false)}
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {items.map((item) => (
                      <CompactEditTile
                        key={item.id}
                        item={item}
                        selected={selectedSet.has(item.id)}
                        onToggle={() => onToggle(item.id)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : selectedEquipment.length === 0 ? (
          <div
            className={cn(
              "flex flex-col items-center justify-center rounded-lg border border-primary/20 bg-dark-slate text-center",
              isMobile ? "gap-3 p-5" : "gap-4 p-6"
            )}
          >
            <Dumbbell className="h-8 w-8 text-gray-light" aria-hidden="true" />
            <div className="space-y-1">
              <p className="font-medium text-white">No equipment selected</p>
              <p className="text-sm text-gray-light">
                Tell us what you have access to so workouts can be tailored to you.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-primary/30 text-primary hover:bg-primary hover:text-black"
              onClick={onEdit}
            >
              Set Equipment
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedEquipment.map((item) => (
              <ViewChip key={item.id} item={item} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
